"use client";

import { useEffect, useRef } from "react";

declare global {
  interface Window {
    googletag?: any;
  }
}

const GPT_SCRIPT_URL = "https://securepubads.g.doubleclick.net/tag/js/gpt.js";
const AD_BASE_PATH = "/23315717725/couponsexperts.com";

let scriptPromise: Promise<void> | null = null;
let idCounter = 0;

function ensureGptScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.googletag?.apiReady) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve) => {
      const existing = document.querySelector<HTMLScriptElement>(`script[src="${GPT_SCRIPT_URL}"]`);
      if (existing) {
        if (window.googletag?.apiReady) resolve();
        else existing.addEventListener("load", () => resolve(), { once: true });
        return;
      }
      const script = document.createElement("script");
      script.src = GPT_SCRIPT_URL;
      script.async = true;
      script.crossOrigin = "anonymous";
      script.onload = () => resolve();
      script.onerror = () => resolve();
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

type AdUnit = "rectangle1" | "rectangle2" | "halfpage" | "billboard";

const UNIT_SIZES: Record<AdUnit, Array<[number, number]>> = {
  rectangle1: [
    [200, 200],
    [300, 50],
    [300, 100],
    [300, 250],
  ],
  rectangle2: [
    [320, 50],
    [320, 100],
    [216, 54],
    [336, 280],
  ],
  halfpage: [
    [120, 600],
    [300, 600],
    [160, 600],
  ],
  billboard: [
    [728, 90],
    [970, 250],
    [970, 90],
  ],
};

interface GptAdProps {
  unit: AdUnit;
  className?: string;
}

export default function GptAd({ unit, className = "" }: GptAdProps) {
  const slotIdRef = useRef<string | null>(null);
  const initializedRef = useRef(false);

  if (slotIdRef.current === null) {
    idCounter += 1;
    slotIdRef.current = `gpt-passback-${unit}-${idCounter}`;
  }

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    let cancelled = false;

    ensureGptScript().then(() => {
      if (cancelled) return;
      const googletag = (window.googletag = window.googletag || { cmd: [] });
      googletag.cmd.push(() => {
        const id = slotIdRef.current;
        if (!id) return;
        const alreadyDefined = googletag
          .pubads()
          .getSlots?.()
          ?.some((slot: any) => slot.getSlotElementId?.() === id);
        if (alreadyDefined) return;
        googletag
          .defineSlot(`${AD_BASE_PATH}/couponsexperts.com_${unit}`, UNIT_SIZES[unit], id)
          .addService(googletag.pubads());
        googletag.enableServices();
        googletag.display(id);
      });
    });

    return () => {
      cancelled = true;
    };
  }, [unit]);

  return (
    <div className={`gpt-ad flex flex-col items-center justify-center ${className}`} aria-label="Advertisement">
      <span className="mb-1 text-[10px] uppercase tracking-widest text-gray-400">Advertisement</span>
      <div id={slotIdRef.current} />
    </div>
  );
}

function OutOfPageAd({ unit, format }: { unit: "anchor" | "interstitial"; format: "BOTTOM_ANCHOR" | "INTERSTITIAL" }) {
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    let cancelled = false;

    ensureGptScript().then(() => {
      if (cancelled) return;
      const googletag = (window.googletag = window.googletag || { cmd: [] });
      googletag.cmd.push(() => {
        const flag = `__gpt_${unit}_defined`;
        const win = window as unknown as Record<string, boolean>;
        if (win[flag]) return;
        win[flag] = true;
        const slot = googletag.defineOutOfPageSlot(
          `${AD_BASE_PATH}/couponsexperts.com_${unit}`,
          googletag.enums.OutOfPageFormat[format]
        );
        if (!slot) return;
        slot.addService(googletag.pubads());
        if (unit === "interstitial") {
          googletag.pubads().set("page_url", window.location.origin);
        }
        googletag.enableServices();
        googletag.display(slot);
      });
    });

    return () => {
      cancelled = true;
    };
  }, [unit, format]);

  return null;
}

export function AdRectangle1({ className = "" }: { className?: string }) {
  return <GptAd unit="rectangle1" className={className} />;
}

export function AdRectangle2({ className = "" }: { className?: string }) {
  return <GptAd unit="rectangle2" className={className} />;
}

export function AdHalfpage({ className = "" }: { className?: string }) {
  return <GptAd unit="halfpage" className={className} />;
}

export function AdBillboard({ className = "" }: { className?: string }) {
  return <GptAd unit="billboard" className={className} />;
}

export function AdAnchor() {
  return <OutOfPageAd unit="anchor" format="BOTTOM_ANCHOR" />;
}

export function AdInterstitial() {
  return <OutOfPageAd unit="interstitial" format="INTERSTITIAL" />;
}
